import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { FaProjectDiagram, FaFolder } from 'react-icons/fa';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { string, number } from 'yup';
import { v4 as uuidv4 } from 'uuid';

import BreadCrumb from '~/components/BreadCrumb';
import ConfirmExitDialog from '~/components/Dialogs/ConfirmExit';
import ConfirmProcedureDialog from '~/components/Dialogs/ConfirmProcedure';
import DefaultInput from '~/components/Inputs/Default';
import ExitButton from '~/components/Buttons/Exit';
import NewProjectInfo from '~/components/ProjectInfo/New';
import SaveButton from '~/components/Buttons/Save';
import Select from '~/components/Select';
import TextArea from '~/components/TextArea';
import UploadFiles from '~/components/UploadFiles';
import UploadRequestFiles from '~/components/UploadRequestFiles';
import WholeNumberInput from '~/components/Inputs/WholeNumber';

import { getNumberFormatValue } from '~/utils';

import { useAuth } from '~/hooks/auth';
import { useToast } from '~/hooks/toast';

import api from '~/services/api';

import { Container, Content, Wrapper } from './styles';

interface IFormProcedure1 {
  item_total_amount: number;
  note: string;
  folder: {
    value: string;
  };
  shipping_type: string;
  first_request: string;
}

interface ILocation {
  erp_client_code?: string;
  erp_client_name?: string;
  erp_client_company_name?: string;
  erp_client_document?: string;
  erp_client_address?: string;
  erp_client_city?: string;
  erp_client_state?: string;
  erp_rep_code: string;
  rep_as_client: boolean;
}

const schema = Yup.object().shape({
  folder: Yup.object().shape({
    value: Yup.string().required('Campo obrigatório'),
  }),
  item_total_amount: Yup.string().when((value: string) => {
    if (Number.isNaN(Number(parseFloat(value)))) {
      return string().required('Quantidade inválida');
    }
    if (parseFloat(value) <= 0) {
      return number()
        .min(1, 'Quantidade inválida')
        .required('Quantidade inválida')
        .typeError('Quantidade inválida');
    }
    return string().notRequired();
  }),
  shipping_type: Yup.string().required('Campo obrigatório'),
  first_request: Yup.string().required('Campo obrigatório'),
});

const Procedure1: React.FC = () => {
  const history = useHistory();
  const { user } = useAuth();
  const { addToast } = useToast();
  const { state } = useLocation<ILocation>();
  const [folders, setFolders] = useState<
    {
      id: string;
      name: string;
    }[]
  >();
  const [loading, setLoading] = useState<boolean>(true);
  const [clientGroup, setClientGroup] = useState();
  const projectTemp = useRef(uuidv4());
  const projectRequestTemp = useRef(uuidv4());
  const [isOpenConfirmProcedure, setIsOpenConfirmProcedure] = useState(false);
  const [isOpenExit, setIsOpenExit] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const { handleSubmit, register, setValue, control, errors } =
    useForm<IFormProcedure1>({ resolver: yupResolver(schema) });

  useEffect(() => {
    const checkEnabledFunctionality = async () => {
      await api.get('/generalsettings').then(response => {
        if (response.data && !response.data.new_folder_req_enabled) {
          history.push('/');
        }
      });
    };

    checkEnabledFunctionality();
  }, [history]);

  useEffect(() => {
    if (state === undefined) {
      addToast({
        type: 'error',
        title: 'Operação inválida!',
        description: 'Cliente não selecionado',
      });

      history.push('/projects/folders');
    } else {
      register('shipping_type');
      register('first_request');

      api.get('/items/folders/valid').then(response => {
        setFolders(response.data);
        setLoading(false);
      });

      api
        .get(
          `/clientgroups/clientcode?erp_client_code=${state.erp_client_code}`,
        )
        .then(response => {
          setClientGroup(response.data);
        });
    }
  }, [addToast, history, state, user, register]);

  const folderOptions = folders?.map(folder => ({
    value: folder.id,
    label: folder.name,
  }));

  const submitForm = useCallback(
    async (data: IFormProcedure1) => {
      try {
        setIsSaving(true);

        await api.post('/projects', {
          ...data,
          item_total_amount: getNumberFormatValue(data.item_total_amount),
          project_request_temp_id: projectRequestTemp.current,
          project_temp_id: projectTemp.current,
          erp_client_code: state.erp_client_code,
          erp_rep_code: state.erp_rep_code,
          record_note: data.note,
          record_description: 'Abertura de solicitação',
          rep_as_client: state.rep_as_client,
          type: 'PASTA',
        });

        addToast({
          type: 'success',
          title: 'Projeto iniciado!',
          description: 'Abertura de projeto realizado com sucesso.',
        });

        history.push('/projects/folders');
      } catch (error: any) {
        setIsSaving(false);

        addToast({
          type: 'error',
          title: 'Ocorreu um erro!',
          description: error.response.data.message,
        });
      }
    },
    [addToast, history, projectRequestTemp, projectTemp, state],
  );

  const onExit = () => {
    api.delete(`projectfilesreceived/projecttempid/${projectTemp.current}`);
    api.delete(
      `projectrequestfilesreceived/projectrequesttempid/${projectRequestTemp.current}`,
    );

    history.push('/projects/folders');
  };

  const handleFiles = (): React.ReactNode | null => {
    if (user.user_type.type !== 'REP') {
      return (
        <Content>
          <UploadFiles
            type="anexo"
            project_temp_id={projectTemp.current}
            project_type="PASTA"
            sequence={1}
            user_id={user.id}
          />
        </Content>
      );
    }

    return null;
  };

  return (
    <Container>
      <BreadCrumb
        main="Solicitações"
        path="/projects/main"
        firstChildPath="/projects/folders"
        firstChild="Pastas"
        secondChild="Nova Solicitação"
        mainIcon={FaProjectDiagram}
        firstChildIcon={FaFolder}
        secondChildIcon={FaFolder}
        mainColored
        firstChildColored
      />

      <NewProjectInfo
        rep_as_client={state?.rep_as_client}
        erp_rep_code={state?.erp_rep_code}
        erp_client_code={state?.erp_client_code}
      />

      <form onSubmit={handleSubmit(submitForm)}>
        <div
          style={{
            maxWidth: 680,
            marginBottom: 20,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 10,
          }}
        >
          <Wrapper style={{ width: 320 }}>
            <Controller
              name="folder"
              control={control}
              options={folderOptions}
              label="Pasta"
              error={errors?.folder?.value?.message}
              as={Select}
              loading={loading}
            />
          </Wrapper>

          <Wrapper style={{ width: 650 }}>
            <DefaultInput
              name="client_group"
              labelFor="client_group"
              labelText="Grupo"
              type="text"
              value={clientGroup}
              disabled
            />
          </Wrapper>

          <Wrapper style={{ width: 320 }}>
            <WholeNumberInput
              name="item_total_amount"
              labelFor="item_total_amount"
              labelText="Quantidade (Unidades)"
              defaultValue={0}
              error={errors?.item_total_amount?.message}
              ref={register}
            />
          </Wrapper>

          <Wrapper style={{ width: 320 }}>
            <Select
              label="Frete"
              options={[
                { value: 'CIF', label: 'CIF' },
                { value: 'FOB', label: 'FOB' },
              ]}
              onSet={(e: any) => {
                setValue('shipping_type', e.value, { shouldValidate: true });
              }}
              error={errors?.shipping_type?.message}
            />
          </Wrapper>

          <Wrapper style={{ width: 320 }}>
            <Select
              label="1º Pedido"
              options={[
                { value: 'Sim', label: 'Sim' },
                { value: 'Não', label: 'Não' },
              ]}
              onSet={(e: any) => {
                setValue('first_request', e.value, { shouldValidate: true });
              }}
              error={errors?.first_request?.message}
            />
          </Wrapper>
        </div>

        <Content>
          <Wrapper style={{ width: '100%' }}>
            <TextArea
              name="note"
              labelFor="note"
              labelText="Observação"
              error={errors?.note?.message}
              ref={register}
              maxLength={1000}
            />
          </Wrapper>
        </Content>

        <Content>
          <UploadRequestFiles
            type="requisicao"
            project_request_temp_id={projectRequestTemp.current}
            project_type="PASTA"
            sequence={1}
            user_id={user.id}
          />
        </Content>

        {handleFiles()}

        <div
          style={{
            margin: 20,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
          }}
        >
          <SaveButton
            type="button"
            processing={isSaving}
            disabled={isSaving}
            onClick={() => {
              setIsOpenConfirmProcedure(true);
            }}
          />

          <ExitButton
            style={{ marginLeft: 10 }}
            type="button"
            disabled={isSaving}
            onClick={() => setIsOpenExit(true)}
          />
        </div>
      </form>

      <ConfirmProcedureDialog
        isOpen={isOpenConfirmProcedure}
        setIsOpen={setIsOpenConfirmProcedure}
        onConfirm={() => handleSubmit(submitForm)()}
        title="Confirma abertura da solicitação?"
        subTitle="A solicitação será enviada para o próximo procedimento"
        opConfirm="Sim"
      />

      <ConfirmExitDialog
        isOpen={isOpenExit}
        setIsOpen={setIsOpenExit}
        onExit={onExit}
      />
    </Container>
  );
};

export default Procedure1;
